import React, { forwardRef, useMemo } from 'react';

type Props = {
  value: string;
  highlightedParts: [number, number][];
  className?: string;
  highlightClass?: string;
} & React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLSpanElement>,
  HTMLSpanElement
>;

export const PartiallyHighlightedText = forwardRef<HTMLSpanElement, Props>(
  ({ className, value, highlightedParts, highlightClass, ...props }, ref) => {
    const highlightData = useMemo(() => {
      const indexes = highlightedParts.slice().sort((a, b) => a[0] - b[0]);
      let lastEnd = 0;
      const formattedParts: { value: string; highlighted?: boolean }[] = [];

      for (const [start, end] of indexes) {
        // Make sure everything not included in `parts` is added
        if (lastEnd < start)
          formattedParts.push({
            value: value.slice(lastEnd, start),
          });

        lastEnd = end;

        formattedParts.push({
          value: value.slice(start, end),
          highlighted: true,
        });
      }

      // If there is anything left after `parts`, add it
      if (value.length > lastEnd)
        formattedParts.push({ value: value.slice(lastEnd) });

      return formattedParts;
    }, [highlightedParts, value]);

    const content = useMemo(() => {
      return highlightData.map(({ value, highlighted }, index) => {
        return (
          <span
            className={highlighted ? highlightClass : undefined}
            key={index}
          >
            {value}
          </span>
        );
      });
    }, [highlightData, highlightClass]);

    return (
      <span ref={ref} className={className} {...props}>
        {content}
      </span>
    );
  },
);
